import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AppButton,
  Box,
  ButtonResponsiveWrapper,
  Checkbox,
  FormProvider,
  getValidationResolver,
  Preloader,
  Text,
  useForm,
  useQueryClient,
  useResponsive,
} from '@common-fe/common-fe';
import { toNumber } from 'lodash';

import { VALIDATORS } from '@/common';
import { CardActivationVariants } from '@/modules/core/components/Dashboard/components/BannersList/CompleteBanner/components/VerifyMyCardModal/VerifyMyCardModal.constants';
import useCardHolderQuery from '@/modules/core/components/Dashboard/components/BannersList/CompleteBanner/useCardHolder.query';
import { ORGANIZATION_VIEW_DATA_QUERY_KEY } from '@/modules/user/queries/useOrganizationViewData.query';

import { useRefreshToken } from '../../SignIn/hooks';

import LimitedAccessVerificationEmailForm from './LimitedAccessVerificationEmailForm';
import { useEmailValidationQuery, useVerifyCardByIdQuery } from './queries';

interface Props {
  onBack: () => void;
  onSuccess: () => void;
  setIsActivationSuccess?: (value: CardActivationVariants) => void;
}
interface FormPayload {
  code: string;
}
const LimitedAccessVerificationEmail: React.FC<Props> = ({ onBack, onSuccess, setIsActivationSuccess }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { isMobile } = useResponsive();
  const [wrongCode, setWrongCode] = useState('');
  const [activateCard, setActivateCard] = useState(false);
  const { handleVerifyCode, isLoading } = useEmailValidationQuery();
  const { cardActivationData } = useCardHolderQuery();
  const { onActivateCardById, isCardActivationLoading } = useVerifyCardByIdQuery();

  const cardId = useMemo(() => cardActivationData?.cardId, [cardActivationData?.cardId]);

  const showActivateCardCheckbox = useMemo(() => cardId && setIsActivationSuccess, [cardId, setIsActivationSuccess]);

  const onSuccessTokenRefresh = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [ORGANIZATION_VIEW_DATA_QUERY_KEY],
    });
  }, [queryClient]);

  const methods = useForm<FormPayload>({
    resolver: getValidationResolver({
      code: VALIDATORS.REQUIRED_STRING.test({
        test: (val) => {
          if (wrongCode === val) {
            return false;
          }
          return true;
        },
        message: t('The code is wrong'),
      }),
    }),
    defaultValues: {
      code: '',
    },
  });

  const { handleRefreshToken } = useRefreshToken();

  const handleActivateCard = useCallback(async () => {
    try {
      if (activateCard && cardId && setIsActivationSuccess) {
        await onActivateCardById(cardId);
        setIsActivationSuccess(CardActivationVariants.ACTIVATED);
      }
    } catch {};
  }, [activateCard, cardId, setIsActivationSuccess, onActivateCardById]);

  const handleSubmit = async (data: FormPayload) => {
    try {
      const response = await handleVerifyCode({
        code: data.code,
        ...cardId ? { card_id: toNumber(cardId) } : {},
      });
      if (response?.data?.verified) {
        await handleActivateCard();
        try {
          await handleRefreshToken();
          onSuccessTokenRefresh();
        } catch {
          //
        }
        onSuccess();
      } else {
        setWrongCode(data.code || '');
      }
    } catch {
      //
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(handleSubmit)}>
        <LimitedAccessVerificationEmailForm />
        <Box
          margin={{ top: isMobile ? 'spacing12' : 'spacing24' }}
          direction={isMobile ? 'column' : 'row'}
          justify={showActivateCardCheckbox ? 'between' : 'end'}
          align={isMobile ? undefined : 'center'}
        >
          {showActivateCardCheckbox ? (
            <Box
              data-testid="limited-access-verification-email_activate-card-checkbox"
              direction="row"
              align="center"
              gap="spacing12"
            >
              <Checkbox
                testId="limited-access-verification-email_activate-card"
                checked={activateCard}
                onChange={setActivateCard}
              >
                <Text size="medium">
                  {t('Activate my card once verified')}
                </Text>
              </Checkbox>
            </Box>
          ) : null}

          <ButtonResponsiveWrapper
            data-testid="limited-access-verification-email_buttons"
            justify="end"
            margin={{ top: isMobile ? 'spacing12' : undefined }}
          >
            <AppButton
              data-testid="limited-access-verification-email_back-button"
              fill={isMobile}
              buttonType="secondary"
              width="160px"
              onClick={onBack}
            >
              {t('Back')}
            </AppButton>
            <AppButton
              data-testid="limited-access-verification-email_confirm-button"
              fill={isMobile}
              disabled={isLoading || isCardActivationLoading}
              type="submit"
              width="160px"
            >
              {isLoading || isCardActivationLoading ? <Preloader /> : t('Confirm')}
            </AppButton>
          </ButtonResponsiveWrapper>
        </Box>
      </form>
    </FormProvider>
  );
};
export default LimitedAccessVerificationEmail;
